import {
  ComponentDescriptor,
  useComponentDescriptor,
  useField,
} from '@kizi/comparator-core';
import { Button, Div, DivProps, Heading, P } from '@kizi/core-components';
import { IconV2 } from '@kizi/shared-components';
import React, { useState } from 'react';
import {
  PersonalDataFieldViewer,
  addHouseNumberAdditionAfterFirstNumber,
} from '../../FormWidget';
import { MobileLayover } from '../../MobileLayover';
import { PersonalData, PersonalDataProps } from './PersonalData';

const Flex = (props) => <Div display="flex" {...props} />;
const ButtonComp = (props) => (
  <Button variant="transparent" fontSize={[2, 2]} pl={1} {...props}>
    {props?.showPencilIcon ? (
      <Div
        display="flex"
        padding="4px"
        borderRadius="80px"
        background="linear-gradient(315deg, #03AD7D 11.93%, #08C98F 81.44%)"
        boxShadow="0px 4px 8px 0px rgba(62, 72, 101, 0.05), 0px 2px 4px 0px rgba(78, 90, 126, 0.10), 0px 4px 8px 0px rgba(0, 0, 0, 0.05)"
      >
        <IconV2 name="pencil" size={24} />
      </Div>
    ) : (
      'Meer'
    )}
  </Button>
);
export interface PersonalDataMinimizedProps
  extends Omit<PersonalDataProps, 'title'> {
  preview: string;
  title: string;
  containerProps?: DivProps;
  groupViewElements?: (ComponentDescriptor | string)[];
  showIcon?: boolean;
  showLocationIcon?: boolean;
  showPencilIcon?: boolean;
  showProvider?: boolean;
  options?: any;
}
export const PersonalDataMinimized: React.FC<PersonalDataMinimizedProps> =
  React.forwardRef<HTMLDivElement, PersonalDataMinimizedProps>(
    (
      {
        preview,
        title,
        containerProps,
        groupViewElements: groupViewModeDescriptor,
        showIcon = false,
        showLocationIcon = false,
        showPencilIcon = false,
        showProvider = false,
        options,
        ...personalDataProps
      },
      ref,
    ) => {
      const [showData, setShowData] = useState(false);
      const toggleLayOver = () => setShowData(!showData);
      if (!preview)
        preview =
          personalDataProps.fieldNames.length > 0
            ? personalDataProps.fieldNames[0]
            : '';
      const field = useField(preview);
      const houseNumberAdditionField = useField(
        'getUserInput.housenumberaddition',
      );
      const currentProviderField = useField('startWidget.currentProvider');

      if (field === undefined) return null;

      const groupViewElements = (groupViewModeDescriptor ?? []).map(
        (descriptor) =>
          typeof descriptor === 'string'
            ? descriptor
            : useComponentDescriptor(descriptor),
      );

      const fieldGroupViewers =
        groupViewElements.length > 0
          ? groupViewElements.map((f, i) =>
              typeof f === 'string' ? (
                <PersonalDataFieldViewer key={i} name={f} />
              ) : (
                f
              ),
            )
          : null;

      const houseNumberAddition = houseNumberAdditionField?.value;
      const fullAddress = houseNumberAddition
        ? addHouseNumberAdditionAfterFirstNumber(
            field.value,
            houseNumberAddition,
          )
        : field.value;

      return (
        <>
          <Div variant="primaryCard" padding={3} {...containerProps} ref={ref}>
            <Flex
              flexDirection="row"
              alignItems={showPencilIcon ? 'center' : 'flex-start'}
              justifyContent="space-between"
            >
              <Div>
                <Flex gridGap={1}>
                  {showLocationIcon && (
                    <IconV2 name="location" size={24} fill="main.2" />
                  )}
                  <P variant="ellipsed">{fieldGroupViewers || fullAddress}</P>
                </Flex>
                {showProvider && options && (
                  <Flex gridGap={1}>
                    <P variant="ellipsed">
                      {`Huidige provider: ${
                        options.filter(
                          (el) =>
                            el.value == currentProviderField?.value?.toString(),
                        )?.[0]?.label
                      }`}
                    </P>
                  </Flex>
                )}
              </Div>
              <ButtonComp
                onClick={toggleLayOver}
                showPencilIcon={showPencilIcon}
              />
            </Flex>
          </Div>
          <MobileLayover
            showIcon={showIcon}
            container={<Div display={['block', 'block', 'block', 'none']} />}
            show={showData}
            onClose={toggleLayOver}
            title={
              <Heading my={[0, 0, 5]} as="h4" variant="H4">
                {title}
              </Heading>
            }
          >
            <PersonalData {...personalDataProps} />
          </MobileLayover>
        </>
      );
    },
  );
